// /help — app 内打开帮助文档. 不再弹外链浏览器, 桌面端体验一致.
//
// 内容来源: docs/help/*.html (npm script sync-help 把它们复制到 public/help/),
// 用 iframe 嵌入, sandbox 允许 same-origin (内容是我们自己的) 不允许 scripts
// (帮助文档纯静态 HTML 无需 JS).
//
// 路由: /help            → index.html 主页
//       /help?page=using-ai → using-ai.html
// 这样 i18n 文字 / SettingsPanel 链接 都可以 deep-link 到指定子页.

import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";

import { useI18n } from "../lib/i18n";
import { resolvedTheme } from "../lib/theme";

const PAGES: { key: string; label: string }[] = [
  { key: "index", label: "首页" },
  { key: "getting-started", label: "快速上手" },
  { key: "using-ai", label: "如何用 AI" },
  { key: "managing-rooms", label: "会话管理" },
  { key: "keyboard-shortcuts", label: "快捷键" },
  { key: "faq", label: "常见问题" },
];

export function HelpPage() {
  const [params, setParams] = useSearchParams();
  const { t } = useI18n();
  const page = params.get("page") || "index";
  // 跟主 app 主题联动: iframe URL 加 ?theme=light/dark, help HTML 内 inline JS
  // 读 query 设 html data-theme. 主题 toggle 时 effect 触发 iframe 重 load.
  const [theme, setTheme] = useState<"light" | "dark">(() => resolvedTheme());
  useEffect(() => {
    // 监听 html data-theme 变化 (ThemeToggle 改它)
    const root = document.documentElement;
    const obs = new MutationObserver(() => setTheme(resolvedTheme()));
    obs.observe(root, { attributes: true, attributeFilter: ["data-theme"] });
    // 也监听系统 dark mode 变化 (用户在 'system' 模式)
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const onSys = () => setTheme(resolvedTheme());
    mq.addEventListener("change", onSys);
    return () => {
      obs.disconnect();
      mq.removeEventListener("change", onSys);
    };
  }, []);
  const src = `/help/${page === "index" ? "index" : page}.html?theme=${theme}`;

  return (
    <section className="page page--help help-layout">
      <header className="help-header">
        <Link to="/rooms" className="link-btn">
          ← {t("nav.rooms")}
        </Link>
        <h1>{t("nav.help")}</h1>
      </header>
      <aside className="help-nav" aria-label="帮助导航">
        <ul>
          {PAGES.map((p) => (
            <li key={p.key}>
              <button
                type="button"
                className={
                  page === p.key ? "help-nav__item help-nav__item--active" : "help-nav__item"
                }
                onClick={() => {
                  const next = new URLSearchParams(params);
                  next.set("page", p.key);
                  setParams(next, { replace: true });
                }}
              >
                {p.label}
              </button>
            </li>
          ))}
        </ul>
      </aside>
      <div className="help-content">
        <iframe
          key={src}
          src={src}
          title={`帮助 - ${page}`}
          sandbox="allow-same-origin"
          className="help-iframe"
        />
      </div>
    </section>
  );
}
